import { SurveyModel, UploadFilesEvent } from 'survey-core';
import { getPresignedUrl } from '../../api/files/get-presigned-url';
import { UploadError, uploadFileToS3 } from '../../api/files/upload-file-to-s3';
import { patchSubmission } from '../../api/submissions/patch-submission';
import { UserType } from '../../types';

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

const FAILED_TO_GET_LINK = 'Failed to get upload link.';

const toUserMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

type FileValue = { name: string; type: string; content: string };

const buildDataAfterUpload = (
  surveyData: Record<string, unknown>,
  questionName: string,
  uploadResults: Array<{ file: File; content: string }>,
) => {
  const currentValue = (surveyData[questionName] as FileValue[] | undefined) ?? [];
  const newFiles = uploadResults.map((r) => ({
    name: r.file.name,
    type: r.file.type,
    content: r.content,
  }));

  return {
    ...surveyData,
    [questionName]: [ ...currentValue, ...newFiles ],
  };
};

export interface UploadFileHandlerProps {
  options: UploadFilesEvent;
  urlSlug: string;
  publicId?: string;
  surveyModel?: SurveyModel;
  isSigner?: boolean;
  trackingId?: string;
}

export const uploadFileHandler = async ({
  options,
  urlSlug,
  publicId,
  trackingId,
  surveyModel,
  isSigner = false,
}: UploadFileHandlerProps) => {
  const uploadPromises = options.files.map(async (file: File) => {
    let response;

    if (trackingId) {
      // Third parties flow
      response = await getPresignedUrl({
        trackingId,
        urlSlug,
        fileName: file.name,
        contentType: file.type,
        fileSize: file.size,
      });
    } else if (publicId) {
      // Submission flow
      response = await getPresignedUrl({
        publicId,
        urlSlug,
        fileName: file.name,
        contentType: file.type,
        fileSize: file.size,
        isSigner,
      });
    } else {
      throw new Error(FAILED_TO_GET_LINK);
    }

    if (!response || !response.success || !response.data) {
      throw new Error(FAILED_TO_GET_LINK);
    }

    const { uploadUrl, fileUrl } = response.data;

    if (!uploadUrl) {
      throw new Error(FAILED_TO_GET_LINK);
    }

    await uploadToS3WithRetries(file, uploadUrl);

    return {
      file,
      content: fileUrl,
      name: file.name,
    };
  });

  const settled = await Promise.allSettled(uploadPromises);
  const results: Array<{ file: File; content: string; name: string }> = [];
  const errors: string[] = [];

  settled.forEach((outcome) => {
    if (outcome.status === 'fulfilled') {
      results.push(outcome.value);
    } else {
      errors.push(toUserMessage(outcome.reason));
    }
  });

  if (results.length > 0 && publicId && surveyModel) {
    const userType = isSigner ? UserType.SIGNER : UserType.INITIAL_USER;
    const updatedData = buildDataAfterUpload(
      surveyModel.data,
      options.name,
      results,
    );
    try {
      await patchSubmission(urlSlug, publicId, updatedData, userType);
    } catch (err) {
      console.error('Failed to save after upload:', err);
    }
  }

  options.callback(results, errors.length > 0 ? errors : undefined);
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const uploadToS3WithRetries = async (file: File, uploadUrl: string): Promise<void> => {
  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    try {
      await uploadFileToS3(file, uploadUrl);
      return;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      if (error instanceof UploadError && !error.retryable) {
        break;
      }

      if (attempt < MAX_RETRIES) {
        console.log(`Upload attempt ${attempt} failed, retrying in ${RETRY_DELAY * attempt}ms...`);
        await sleep(RETRY_DELAY * attempt);
      }
    }
  }

  throw lastError;
};
